import { type ReactElement, type MouseEvent as ReactMouseEvent } from 'react';
import {
  NeonButtonFooter,
  NeonLoadingIndicator,
  NeonModalDialog,
  NeonRadioField,
  NeonSelectField,
} from '@ps-refarch-ux/neon';
import type { RadioOption } from '@ps-refarch-ux/neon';
import type { PlanOfStudySummary } from '@/types/plans-of-study';
import styles from './create-plan-modal.module.scss';

export type CreatePlanModalChoice =
  | { mode: 'new' }
  | { mode: 'copy'; planId: string; planName: string };

export interface CreatePlanModalProps {
  isOpen: boolean;
  modalId: string;
  /** Selected mode before continue, or null. */
  selectedMode: 'new' | 'copy' | null;
  selectedPlanId: string | null;
  plans: PlanOfStudySummary[];
  isPlansLoading?: boolean;
  onClose: () => void;
  onContinue: (choice: CreatePlanModalChoice) => void;
  onModeChange: (mode: 'new' | 'copy') => void;
  onPlanSelect: (planId: string | null) => void;
}

const CREATE_NEW = 'new';
const COPY_EXISTING = 'copy';

const CREATE_PLAN_MODE_OPTIONS: RadioOption[] = [
  { id: 'create-plan-option-new', value: CREATE_NEW, labelText: 'Create a new plan.' },
  {
    id: 'create-plan-option-copy',
    value: COPY_EXISTING,
    labelText: 'Copy an existing plan and edit it.',
  },
];

export function CreatePlanModal({
  isOpen,
  modalId,
  selectedMode,
  selectedPlanId,
  plans,
  isPlansLoading = false,
  onClose,
  onContinue,
  onModeChange,
  onPlanSelect,
}: CreatePlanModalProps): ReactElement | null {
  if (!isOpen) {
    return null;
  }

  const canContinue =
    selectedMode === CREATE_NEW || (selectedMode === COPY_EXISTING && !!selectedPlanId);
  const planOptions = plans.map((p) => ({ text: p.name, value: p.id }));

  const handleContinue = () => {
    if (selectedMode === CREATE_NEW) {
      onContinue({ mode: 'new' });
    } else if (selectedMode === COPY_EXISTING && selectedPlanId) {
      const plan = plans.find((p) => p.id === selectedPlanId);
      onContinue({
        mode: 'copy',
        planId: selectedPlanId,
        planName: plan?.name ?? 'Plan',
      });
    }
  };

  return (
    <NeonModalDialog id={modalId} neonDialogHasClosed={onClose} dataSize="small">
      <div data-slot="dialog-header-title">Create plan of study</div>
      <div className={styles.body} data-slot="dialog-body">
        <p className={styles.instruction}>Choose how to create the plan of study:</p>
        <NeonRadioField
          id="create-plan-mode"
          dataLabelText="Create or copy plan"
          dataShowLabel={false}
          modelValue={selectedMode ?? ''}
          modelValueChange={(value) => {
            if (value === CREATE_NEW || value === COPY_EXISTING) {
              onModeChange(value);
            }
          }}
          options={CREATE_PLAN_MODE_OPTIONS}
          dataControlLayout="column"
        />

        {selectedMode === COPY_EXISTING &&
          (isPlansLoading ? (
            <div
              className={styles.plansLoading}
              data-testid="create-plan-plans-loading"
              aria-busy="true"
            >
              <NeonLoadingIndicator dataSize="medium" />
            </div>
          ) : (
            <NeonSelectField
              modelValue={selectedPlanId ?? undefined}
              modelValueChange={(value: string | undefined) => onPlanSelect(value ?? null)}
              options={planOptions}
              id="create-plan-copy-select"
              dataLabelText="Plan to copy"
            />
          ))}
      </div>
      <div data-slot="dialog-button-footer">
        <NeonButtonFooter
          id="create-plan-modal-footer"
          buttons={[
            {
              id: 'create-plan-cancel',
              text: 'Cancel',
              type: 'borderless',
              onClick: (event: Event | ReactMouseEvent) => {
                const target = (event?.target ?? (event as ReactMouseEvent)?.currentTarget) as
                  | HTMLElement
                  | undefined;
                target?.dispatchEvent(new Event('neonDialogClose', { bubbles: true }));
              },
            },
          ]}
          primaryButton={{
            id: 'create-plan-continue',
            text: 'Continue',
            type: 'primary',
            disabled: !canContinue || isPlansLoading,
            onClick: handleContinue,
          }}
        />
      </div>
    </NeonModalDialog>
  );
}
